import I18n from 'I18n';
import { useMemo, useState } from 'react';
import UIList from 'UIComponents/list/UIList';
import UIIcon from 'UIComponents/icon/UIIcon';
import { PlaylistAssignmentProgress, useFilteredPlaylistAssignmentsForPortal } from '..';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedDateTime from 'I18n/components/FormattedDateTime';
import FormattedPercentage from 'I18n/components/FormattedPercentage';
import styled from 'academy-common-lib/utils/Styling';
import { TABLE_PAGINATOR_SIZE, Table } from '../../table';
import { useFullPlaylist } from '../../savedItems';
import UITruncateString from 'UIComponents/text/UITruncateString';
import UIPaginator from 'UIComponents/paginator/UIPaginator';
import formatName from 'I18n/utils/formatName';
import CompletedIcon from '../../../components/ContentCards/ReducedContentCard/subcomponents/CompletedIcon';
import { AssignPlaylistButton } from './AssignPlaylistButton';
import { UnAssignPlaylistButton } from './UnAssignPlaylistButton';
import { useHasGate } from '../../accounts';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const PlaylistItemHeader = styled.th.withConfig({
  displayName: "PlaylistAssignmentsForPortalTable__PlaylistItemHeader",
  componentId: "sc-1pey585-0"
})(["min-width:200px;"]);
const PlaylistAssignmentsForPortalTableHeaderActions = ({
  selectedRows,
  deselectAllRows,
  playlistId,
  portalId
}) => {
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx("small", {
      style: {
        textTransform: 'none',
        fontWeight: 400
      },
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `learningCenter.playlistAssignments.assignmentsSelected`,
        options: {
          count: selectedRows.length
        }
      })
    }), /*#__PURE__*/_jsxs(UIList, {
      childClassName: "m-left-3",
      inline: true,
      children: [/*#__PURE__*/_jsx(UnAssignPlaylistButton, {
        assignments: selectedRows.map(id => Number(id)),
        onDeleted: deselectAllRows
      }), /*#__PURE__*/_jsxs(AssignPlaylistButton, {
        assignmentIds: selectedRows.map(id => Number(id)),
        playlistId: playlistId,
        portalId: portalId,
        use: "link",
        children: [/*#__PURE__*/_jsx(UIIcon, {
          name: "add"
        }), /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.playlistAssignments.addDueDates"
        })]
      })]
    })]
  });
};
const PlaylistAssignmentsForPortalTableHeader = ({
  playlistId
}) => {
  const {
    playlist
  } = useFullPlaylist(playlistId);
  const hasShortsGate = useHasGate('Academy:ShortFormContent');
  if (!playlist) return null;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx("th", {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "learningCenter.playlistAssignments.columns.user"
      })
    }), /*#__PURE__*/_jsx("th", {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "learningCenter.playlistAssignments.columns.dueDate"
      })
    }), playlist.items.map(({
      itemId,
      name,
      type
    }) => (hasShortsGate || type !== 'SHORT_FORM_CONTENT') && /*#__PURE__*/_jsx(PlaylistItemHeader, {
      children: /*#__PURE__*/_jsx(UITruncateString, {
        matchContentWidth: true,
        children: name
      })
    }, itemId))]
  });
};
const PlaylistAssignmentsForPortalTableRow = assignment => {
  const {
    assignee: {
      firstName,
      lastName,
      userId
    },
    dueDate,
    playlistId,
    portalId,
    progress,
    id
  } = assignment;
  const displayName = formatName({
    firstName,
    lastName
  });
  const {
    playlist
  } = useFullPlaylist(playlistId);
  const {
    itemsProgress
  } = progress || {
    itemsProgress: []
  };
  const hasShortsGate = useHasGate('Academy:ShortFormContent');
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx("td", {
      children: /*#__PURE__*/_jsx(PlaylistAssignmentProgress, {
        assignment: assignment
      })
    }), /*#__PURE__*/_jsx("td", {
      children: /*#__PURE__*/_jsx("div", {
        style: {
          fontWeight: 500
        },
        children: /*#__PURE__*/_jsx(AssignPlaylistButton, {
          assignmentIds: [id],
          playlistId: playlistId,
          portalId: portalId,
          use: "link",
          children: dueDate ? /*#__PURE__*/_jsx(FormattedDateTime, {
            format: "L",
            type: "utc",
            value: dueDate
          }) : /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.common.add"
          })
        })
      })
    }), playlist && playlist.items.map(({
      itemId,
      name,
      type,
      language
    }) => {
      const itemProgress = itemsProgress.find(ip => ip.itemId === itemId && ip.type === type && ip.language === language);
      if (!hasShortsGate && type === 'SHORT_FORM_CONTENT') return null;
      return /*#__PURE__*/_jsx("td", {
        title: I18n.text('learningCenter.playlistAssignments.assignmentProgressLabel', {
          itemName: name,
          assigneeName: displayName
        }),
        children: itemProgress && itemProgress.status !== 'NOT_STARTED' ? itemProgress.status === 'COMPLETED' ? /*#__PURE__*/_jsx(CompletedIcon, {}) : /*#__PURE__*/_jsx(FormattedPercentage, {
          value: itemProgress.progressLevel
        }) : null
      }, `${portalId}_${userId}_${itemId}`);
    })]
  });
};
const PlaylistAssignmentsForPortalTable = ({
  playlistId,
  portalId
}) => {
  const [page, setPage] = useState(1);
  const {
    playlistAssignmentsForPortal,
    pageCount
  } = useFilteredPlaylistAssignmentsForPortal({
    playlistId,
    portalId,
    page
  });
  const rows = useMemo(() => {
    const assignmentsRecord = new Map();
    if (playlistAssignmentsForPortal) playlistAssignmentsForPortal.forEach(assignment => {
      assignmentsRecord.set(assignment.id.toString(), assignment);
    });
    return assignmentsRecord;
  }, [playlistAssignmentsForPortal]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(Table, {
      renderHeader: () => /*#__PURE__*/_jsx(PlaylistAssignmentsForPortalTableHeader, {
        playlistId: playlistId
      }),
      renderHeaderActions: ({
        selectedRows,
        deselectAllRows
      }) => /*#__PURE__*/_jsx(PlaylistAssignmentsForPortalTableHeaderActions, {
        deselectAllRows: deselectAllRows,
        playlistId: playlistId,
        portalId: portalId,
        selectedRows: selectedRows
      }),
      renderRow: data => /*#__PURE__*/_jsx(PlaylistAssignmentsForPortalTableRow, Object.assign({}, data)),
      rows: rows
    }), pageCount > 1 && /*#__PURE__*/_jsx(UIPaginator, {
      maxVisiblePageButtons: TABLE_PAGINATOR_SIZE,
      onPageChange: ({
        target: {
          value
        }
      }) => setPage(value),
      page: page,
      pageCount: pageCount
    })]
  });
};
export default PlaylistAssignmentsForPortalTable;